import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Link,
  Modal,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import axios from "axios";
import { baseUrl } from "../../../helper/urls";
import LinkIcon from "@mui/icons-material/Link";
import LaunchIcon from "@mui/icons-material/Launch";

// Arrow Button for Slider
const ArrowButton = ({ onClick, direction }) => (
  <div
    onClick={onClick}
    style={{
      position: "absolute",
      top: "50%",
      transform: "translateY(-50%)",
      zIndex: 2,
      fontSize: "32px",
      color: "#124178",
      cursor: "pointer",
      padding: "5px 10px",
      userSelect: "none",
      [direction]: "-25px",
    }}
  >
    {direction === "left" ? "‹" : "›"}
  </div>
);

export default function WebinarCarousel({ webinarData }) {
  const { token, name, email } = useSelector((state) => state.auth);

  const [open, setOpen] = useState(false);
  const [selectedWebinarId, setSelectedWebinarId] = useState(null);
  const [selectedContent, setselectedContent] = useState("");
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setNewName(name ?? "");
      setNewEmail(email ?? "");
    }
  }, [open, name, email]);

  const handleOpen = (item) => {
    setSelectedWebinarId(item.id);
    setselectedContent(item.content);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setMessage("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newName.trim() || !newEmail.trim() || !message.trim()) {
      toast.error("All fields are required.");
      return;
    }

    setBusy(true);
    try {
      await axios.post(
        `${baseUrl}/api/booking_req/requestMailSent`,
        {
          name: newName,
          email: newEmail,
          message,
          description: selectedContent,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      toast.success("Email sent – see you there!");
      handleClose();
    } catch (err) {
      toast.error(
        `Couldn't send Email: ${
          err.response ? err.response.statusText : err.message
        }`
      );
    } finally {
      setBusy(false);
    }
  };

  const isSingleItem = webinarData?.length <= 1;

  const settings = {
    dots: !isSingleItem,
    infinite: !isSingleItem,
    slidesToShow: 1,
    autoplay: !isSingleItem,
    autoplaySpeed: 4000,
    speed: 500,
    arrows: !isSingleItem,
    nextArrow: <ArrowButton direction="right" />,
    prevArrow: <ArrowButton direction="left" />,
    appendDots: (dots) => (
      <div style={{ marginTop: "12px" }}>
        <ul style={{ display: "flex", justifyContent: "center", gap: "8px" }}>
          {dots}
        </ul>
      </div>
    ),
    customPaging: () => <div className="custom-dot" />,
  };

  const TruncatedText = ({ content, maxLength = 105 }) => {
    const [expanded, setExpanded] = useState(false);

    const toggleExpanded = () => setExpanded((prev) => !prev);

    const isLong = content.length > maxLength;
    const displayedText =
      expanded || !isLong ? content : `${content.slice(0, maxLength)}...`;

    return (
      <>
        <Typography fontSize={14} color="#374151" lineHeight={1.6} mb={"1px"}>
          <strong>Coming Soon:</strong>
          {displayedText}
        </Typography>
        {isLong && (
          <Button
            size="small"
            onClick={toggleExpanded}
            sx={{ textTransform: "none", p: 0, minWidth: "auto", mb: "6px" }}
          >
            {expanded ? "Read Less" : "Read More"}
          </Button>
        )}
      </>
    );
  };

  return (
    <>
      <Box sx={{ maxWidth: 440, mx: "auto", position: "relative" }}>
        {webinarData && webinarData.length > 0 ? (
          <Slider {...settings}>
            {webinarData.map((item) => (
              <Box key={item.id} px={1}>
                <Paper
                  elevation={0}
                  sx={{
                    bgcolor: "#fff7e6",
                    borderLeft: "5px solid #f59e0b",
                    borderRadius: 2,
                    p: 1,
                    mt: 1,
                  }}
                >
                  <TruncatedText content={item.content} />

                  <Typography
                    fontSize={12}
                    color="#374151"
                    // lineHeight={1.6}
                    // mb={1}
                  >
                    <strong>📅 Date:</strong> {item.date}
                  </Typography>
                  <Typography
                    fontSize={12}
                    color="#374151"
                    // lineHeight={1.6}
                    // mb={1}
                  >
                    <strong>🕑 Time:</strong> {item.time} PST
                  </Typography>
                  <Typography
                    fontSize={12}
                    color="#374151"
                    // lineHeight={1.6}
                    // mb={1}
                  >
                    <strong>📍 Join via Google Meet:</strong>{" "}
                    <a
                      href={item.meet_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "#1e3a8a", textDecoration: "none" }}
                    >
                      Meet Link
                    </a>
                  </Typography>

                  <Link
                    component="button"
                    underline="hover"
                    sx={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: "#1e3a8a",
                      mt: 1,
                      display: "inline-block",
                    }}
                    onClick={() => handleOpen(item)}
                  >
                    👉 Reserve Your Spot
                  </Link>
                </Paper>
              </Box>
            ))}
          </Slider>
        ) : (
          <Typography textAlign="center" fontSize={14} color="gray">
            No upcoming webinars.
          </Typography>
        )}
      </Box>

      {/* Modal */}
      <Modal open={open} onClose={handleClose}>
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "#fff",
            borderRadius: 2.5,
            boxShadow: "0 8px 20px rgba(0,0,0,0.2)",
            p: 3,
            width: "90%",
            maxWidth: 500,
            outline: "none",
          }}
        >
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{ position: "absolute", top: 8, right: 8, color: "#aaa" }}
          >
            <CloseIcon fontSize="large" />
          </IconButton>

          <Typography variant="h6" color="#1e3a8a" mb={1}>
            Reserve Your Spot
          </Typography>
          <Typography mb={2}>
            Fill in your info&nbsp;and we’ll send you a reminder + join link.
          </Typography>

          <TextField
            fullWidth
            required
            label="Full Name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            variant="outlined"
            size="small"
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            required
            label="Email Address"
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            variant="outlined"
            size="small"
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            required
            label="Any specific questions for the webinar?"
            multiline
            rows={3}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            variant="outlined"
            size="small"
          />

          <Button
            type="submit"
            variant="contained"
            disabled={busy}
            sx={{
              mt: 3,
              bgcolor: "#1e3a8a",
              "&:hover": { bgcolor: "#294cba" },
              borderRadius: 2,
              px: 3,
            }}
          >
            {busy ? "Sending…" : "Submit"}
          </Button>
        </Box>
      </Modal>

      {/* Custom dot styles */}
      <style>{`
        .custom-dot {
          width: 8px;
          height: 8px;
          background-color: #124178;
          border-radius: 50%;
        }
        .slick-dots li.slick-active .custom-dot {
          background-color: #ff5722;
          width: 10px;
          height: 10px;
        }
      `}</style>
    </>
  );
}

// import React, { useEffect, useState } from "react";
// import {
//   Box,
//   Button,
//   IconButton,
//   Link,
//   Modal,
//   Paper,
//   TextField,
//   Typography,
// } from "@mui/material";
// import CloseIcon from "@mui/icons-material/Close";
// import Slider from "react-slick";
// import "slick-carousel/slick/slick.css";
// import "slick-carousel/slick/slick-theme.css";
// import { useSelector } from "react-redux";
// import { toast } from "react-toastify";
// import axios from "axios";
// import { baseUrl } from "../../../helper/urls";

// const ArrowButton = ({ onClick, direction }) => (
//   <div
//     onClick={onClick}
//     style={{
//       position: "absolute",
//       top: "50%",
//       transform: "translateY(-50%)",
//       zIndex: 2,
//       fontSize: "32px",
//       color: "#124178",
//       cursor: "pointer",
//       padding: "5px 10px",
//       userSelect: "none",
//       [direction]: "-25px",
//     }}
//   >
//     {direction === "left" ? "‹" : "›"}
//   </div>
// );

// export default function WebinarCarousel({ webinarData }) {
//   //   const { token } = useSelector((state) => state.auth);
//   const { token, name, email } = useSelector((state) => state.auth);

//   const [open, setOpen] = useState(false);
//   const [selectedWebinarId, setSelectedWebinarId] = useState(null);
//   const [selectedContent, setselectedContent] = useState("");
//   console.log("selectedContent: ", selectedContent);
//   const [newName, setNewName] = useState("");
//   const [newEmail, setNewEmail] = useState("");
//   const [message, setMessage] = useState("");
//   const [busy, setBusy] = useState(false);

//   useEffect(() => {
//     if (!open) return;

//     setNewName(name ?? "");
//     setNewEmail(email ?? "");
//   }, [open]);

//   const handleOpen = (item) => {
//     setSelectedWebinarId(item.id);
//     setselectedContent(item.content);
//     setOpen(true);
//   };
//   const handleClose = () => {
//     setOpen(false);
//     setMessage("");
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     if (!newName.trim() || !newEmail.trim() || !message.trim()) {
//       toast.error("All fields are required.");
//       return;
//     }

//     setBusy(true);
//     try {
//       await axios.post(
//         `${baseUrl}/api/booking_req/requestMailSent`,
//         {
//           name: newName,
//           email: newEmail,
//           message,
//           description: selectedContent,
//         },
//         {
//           headers: {
//             "Content-Type": "application/json",
//             Authorization: `Bearer ${token}`,
//           },
//         }
//       );

//       toast.success("Email sent – see you there!");
//       handleClose();
//     } catch (err) {
//       toast.error(
//         `Couldn't send Email: ${
//           err.response ? err.response.statusText : err.message
//         }`
//       );
//     } finally {
//       setBusy(false);
//     }
//   };

//   const settings = {
//     dots: true,
//     infinite: true,
//     slidesToShow: 1,
//     autoplay: true,
//     autoplaySpeed: 4000,
//     speed: 500,
//     arrows: true,
//     nextArrow: <ArrowButton direction="right" />,
//     prevArrow: <ArrowButton direction="left" />,
//     appendDots: (dots) => (
//       <div style={{ marginTop: "12px" }}>
//         <ul style={{ display: "flex", justifyContent: "center", gap: "8px" }}>
//           {dots}
//         </ul>
//       </div>
//     ),
//     customPaging: () => <div className="custom-dot" />,
//   };

//   return (
//     <>
//       <Box sx={{ maxWidth: 440, mx: "auto", position: "relative" }}>
//         <Slider {...settings}>
//           {webinarData.map((item) => (
//             <Box key={item.id} px={1}>
//               <Paper
//                 elevation={0}
//                 sx={{
//                   bgcolor: "#fff7e6",
//                   borderLeft: "5px solid #f59e0b",
//                   borderRadius: 2,
//                   p: 2,
//                   mt: 1,
//                 }}
//               >
//                 <Typography
//                   fontSize={14}
//                   color="#374151"
//                   lineHeight={1.6}
//                   mb={1}
//                 >
//                   Coming Soon: {item.content}
//                 </Typography>

//                 <Link
//                   component="button"
//                   underline="hover"
//                   sx={{
//                     fontSize: 14,
//                     fontWeight: 600,
//                     color: "#1e3a8a",
//                     mt: 1,
//                     display: "inline-block",
//                   }}
//                   onClick={() => handleOpen(item)}
//                 >
//                   👉 Reserve Your Spot
//                 </Link>
//               </Paper>
//             </Box>
//           ))}
//         </Slider>
//       </Box>

//       {/* Modal */}
//       <Modal open={open} onClose={handleClose}>
//         <Box
//           component="form"
//           onSubmit={handleSubmit}
//           sx={{
//             position: "absolute",
//             top: "50%",
//             left: "50%",
//             transform: "translate(-50%, -50%)",
//             bgcolor: "#fff",
//             borderRadius: 2.5,
//             boxShadow: "0 8px 20px rgba(0,0,0,0.2)",
//             p: 3,
//             width: "90%",
//             maxWidth: 500,
//             outline: "none",
//           }}
//         >
//           <IconButton
//             aria-label="close"
//             onClick={handleClose}
//             sx={{ position: "absolute", top: 8, right: 8, color: "#aaa" }}
//           >
//             <CloseIcon fontSize="large" />
//           </IconButton>

//           <Typography variant="h6" color="#1e3a8a" mb={1}>
//             Reserve Your Spot
//           </Typography>
//           <Typography mb={2}>
//             Fill in your info&nbsp;and we’ll send you a reminder + join link.
//           </Typography>

//           <TextField
//             fullWidth
//             required
//             label="Full Name"
//             value={newName}
//             onChange={(e) => setNewName(e.target.value)}
//             variant="outlined"
//             size="small"
//             sx={{ mb: 2 }}
//           />
//           <TextField
//             fullWidth
//             required
//             label="Email Address"
//             type="newEmail"
//             value={newEmail}
//             onChange={(e) => setNewEmail(e.target.value)}
//             variant="outlined"
//             size="small"
//             sx={{ mb: 2 }}
//           />
//           <TextField
//             fullWidth
//             required
//             label="Any specific questions for the webinar?"
//             multiline
//             rows={3}
//             value={message}
//             onChange={(e) => setMessage(e.target.value)}
//             variant="outlined"
//             size="small"
//           />

//           <Button
//             type="submit"
//             variant="contained"
//             disabled={busy}
//             sx={{
//               mt: 3,
//               bgcolor: "#1e3a8a",
//               "&:hover": { bgcolor: "#294cba" },
//               borderRadius: 2,
//               px: 3,
//             }}
//           >
//             {busy ? "Sending…" : "Submit"}
//           </Button>
//         </Box>
//       </Modal>

//       {/* Custom dot styles */}
//       <style>{`
//         .custom-dot {
//           width: 8px;
//           height: 8px;
//           background-color: #124178;
//           border-radius: 50%;
//         }
//         .slick-dots li.slick-active .custom-dot {
//           background-color: #ff5722;
//           width: 10px;
//           height: 10px;
//         }
//       `}</style>
//     </>
//   );
// }
